import { css } from '@styled-system/css';
import { center } from '@styled-system/patterns';
import { token } from '@styled-system/tokens';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PuffLoader } from 'react-spinners';

import { useAnalysisPageData } from '@/features/analysisPage/hooks/useAnalysisPageData';
import { useAnalysisTabRedirect } from '@/features/analysisPage/hooks/useAnalysisTabRedirect';
import { AnalysisPageNiches } from '@/features/analysisPageNiches/components/AnalysisPageNiches';
import { AnalysisPageSellsHist } from '@/features/analysisPageSellsHist/components/AnalysisPageSellsHist';
import { AnalysisPageStatistics } from '@/features/analysisPageStatistics/components/AnalysisPageStatistics';
import { Message } from '@/shared/components/ui/Message';

import { getAnalysisIntent } from '@/shared/utils/analysis/searchIntent';

import { AnalysisKeyStatsStrip } from './AnalysisKeyStatsStrip';
import { AnalysisPageHeader } from './AnalysisPageHeader';
import { AnalysisPageTabs } from './AnalysisPageTabs';

interface AnalysisPageProps {
  analysisId: string;
  onBackToMenu?: () => void;
}

const NoContentMessage = ({ message }: { message: string }) => (
  <div className={center({ w: 'full', h: 'full' })}>
    <Message type="error" bordered>
      {message}
    </Message>
  </div>
);

export const AnalysisPage: FC<AnalysisPageProps> = ({ analysisId, onBackToMenu }) => {
  const { t } = useTranslation();
  const { analysisHeaderData, activeTab, handleRename, handleDelete, isLoading, error, isUpdating } =
    useAnalysisPageData(analysisId);

  // Redirection vers l'onglet par défaut selon l'intention de la recherche
  // (large → niches, ciblée → historique) — attend que pageUrl soit chargée.
  useAnalysisTabRedirect(analysisId, analysisHeaderData);

  const searchIntent = getAnalysisIntent(analysisHeaderData);

  const pageContainerStyles = css({
    w: '100%',
    h: '100vh',
    display: 'flex',
    flexDirection: 'column',
    bg: 'base.bg', // #1a1a1a
    overflow: 'hidden',
  });

  const centerContentStyles = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    h: '100%',
    p: '2rem',
  });

  if (isLoading) {
    return (
      <div className={pageContainerStyles}>
        <div
          className={center({
            w: 'full',
            h: 'full',
          })}
        >
          <PuffLoader color={token.var('colors.accent.primary')} size={60} />
        </div>
      </div>
    );
  }

  if (error || !analysisHeaderData) {
    return (
      <div className={pageContainerStyles}>
        <div className={centerContentStyles}>
          <Message type="error" bordered>
            {error || t('analysisDetail.notFound')}
          </Message>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'statistics':
        return <AnalysisPageStatistics />;
      case 'niches':
        return <AnalysisPageNiches />;
      case 'history':
        return <AnalysisPageSellsHist />;
      default:
        return <NoContentMessage message={t('analysisDetail.selectTab')} />;
    }
  };


  return (
    <div className={pageContainerStyles}>
      <AnalysisPageHeader
        analysis={analysisHeaderData}
        handleRename={handleRename}
        handleDelete={handleDelete}
        isUpdating={isUpdating}
        onBackToMenu={onBackToMenu}
      />
      {searchIntent === 'discovery' && <AnalysisKeyStatsStrip analysisId={analysisId} />}
      <AnalysisPageTabs>{renderTabContent()}</AnalysisPageTabs>
    </div>
  );
};
